import { Box, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectConfigurationParameterById } from '!core/redux';
import {
  TrlFeedback,
  TrlScreenSummary,
  TrlTextWithLink,
} from '»components/atoms';
import { isStinvestActiveClient } from '»utils/clientsUtils';
import {
  useAppBarContext,
  useCurrentPage,
  usePageNavigation,
} from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { AppCard } from '../../app/AppCard';
import { TrlNavigationFooter } from '../molecules/TrlNavigationFooter';
import {
  AuthContainer,
  InnerFormContainer,
  OuterFormContainer,
} from './AuthWrapper.styles';
import { onboardingMainFormFieldsSelector } from './Onboarding/redux';

export const AuthWrapper = ({
  screenTitle,
  screenDescr,
  screenSecondDescr,
  screenNotice,
  linkText,
  linkRoute,
  linkMenuItem,
  children,
  showFeedbackMsg,
  feedbackMsg,
  handleCloseFeedback,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  const hasOnboarding =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING'),
    )?.value === 'Y';
  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);
  const isContractPreviewEnabled =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING_CONTRACT_PREVIEW'),
    ).value === 'Y';

  const { navigateToPage } = usePageNavigation();
  const { barHeight } = useAppBarContext();
  const {
    isOnboardingStinvestFirstStep,
    isOnboardingCreateAccountStep,
    isOnboardingAdvancedSteps,
    isOnboardingDocumentAcceptance,
    isOnboardingDocumentsAttach,
    isLoginPage,
    isOnboardingPage,
    isOnboardingMainFormStep,
  } = useCurrentPage(location.pathname);

  const isNotStinvestActiveClient = !isStinvestActiveClient;

  const isLoginOrOnboardingInitialSteps =
    isLoginPage ||
    isOnboardingStinvestFirstStep ||
    isOnboardingCreateAccountStep;

  const isNotStinvestCreateAccountStep =
    isNotStinvestActiveClient && isOnboardingCreateAccountStep;

  if (!isLoginPage && !isOnboardingPage) return null;

  return (
    <>
      <AuthContainer
        barHeight={barHeight}
        isLoginOrOnboardingInitialSteps={isLoginOrOnboardingInitialSteps}
        isNotStinvestCreateAccountStep={isNotStinvestCreateAccountStep}
        isOnboardingAdvancedSteps={isOnboardingAdvancedSteps}
        isOnboardingDocumentAcceptance={isOnboardingDocumentAcceptance}
        isContractPreviewEnabled={isContractPreviewEnabled}
      >
        {(!isMobile || (isLoginOrOnboardingInitialSteps && isMobile)) && (
          <AppCard />
        )}
        <OuterFormContainer
          id="outerFormContainer"
          barHeight={barHeight}
          isOnboardingAdvancedSteps={isOnboardingAdvancedSteps}
          isOnboardingDocumentAcceptance={isOnboardingDocumentAcceptance}
          isContractPreviewEnabled={isContractPreviewEnabled}
          isOnboardingMainFormStep={isOnboardingMainFormStep}
          hasTopPadding={
            isLoginPage ? hasOnboarding : !isOnboardingDocumentAcceptance
          }
          formFieldsAreLoaded={mainFormFields.length > 0}
        >
          <InnerFormContainer
            id="innerFormContainer"
            isOnboardingAdvancedSteps={isOnboardingAdvancedSteps}
            isOnboardingDocumentAcceptance={isOnboardingDocumentAcceptance}
            isOnboardingDocumentsAttach={isOnboardingDocumentsAttach}
          >
            <TrlTextWithLink
              mainText={screenSecondDescr}
              linkText={linkText}
              handleLinkClick={() => {
                navigateToPage({
                  route: linkRoute,
                  menuItem: linkMenuItem,
                });
              }}
            />
            <TrlScreenSummary title={screenTitle}>
              <TrlTextWithLink mainText={screenDescr}>
                {screenNotice && (
                  <Box component="span">
                    {`\n\n${TrlFormattedMessagePlain({ id: screenNotice })}`}
                  </Box>
                )}
              </TrlTextWithLink>
            </TrlScreenSummary>
            {showFeedbackMsg && (
              <TrlFeedback msg={feedbackMsg} onClose={handleCloseFeedback} />
            )}
            {children}
            <TrlTextWithLink
              mainText={screenSecondDescr}
              linkText={linkText}
              handleLinkClick={() => {
                navigateToPage({
                  route: linkRoute,
                  menuItem: linkMenuItem,
                });
              }}
            />
          </InnerFormContainer>
        </OuterFormContainer>
      </AuthContainer>
      {isMobile && (
        <TrlNavigationFooter
          isOnlyForNavigation
          navigationLabel="TRLT_UI-HOME_PAGE"
        />
      )}
    </>
  );
};

AuthWrapper.propTypes = {
  screenTitle: PropTypes.string.isRequired,
  screenDescr: PropTypes.string.isRequired,
  screenNotice: PropTypes.string,
  screenSecondDescr: PropTypes.string,
  linkText: PropTypes.string,
  linkRoute: PropTypes.string,
  linkMenuItem: PropTypes.string,
  children: PropTypes.element.isRequired,
  showFeedbackMsg: PropTypes.bool.isRequired,
  feedbackMsg: PropTypes.string.isRequired,
  handleCloseFeedback: PropTypes.func,
};
